import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import TrashCanIcon from '../asset/TrashCanIcon'
import { AddBtn } from './TableAddRowButton'
import { gql, useQuery } from '@apollo/client'
import { ISeasonalPVCharge } from '../util/algorithm/algoInterface'
import {
  PurchaseUnitComparisonTitle,
  StyledTotalPurchaseValue,
} from '../style/TotalPurchaseValue.styled'

const StyledSelect = styled.select<{ isSelected: boolean }>`
  ${({ isSelected }) =>
    isSelected &&
    `
    font-weight: 900;
  `}
`

//#region Types
export type CustomerLists = {
  _id: string
  name: string
  facilityCapacity?: number
  previousMethod?: string
  revenueContractType?: string
  contractPrice?: number
  RECWeight?: number
}

export type PlanLists = { _id: string; name: string; custId?: string }

export type TotalPurchaseValuProps = {
  setIDList?: React.Dispatch<React.SetStateAction<buyTOUList>>
  buyTOUList: buyTOUList[]
  setBuyTOUList: React.Dispatch<React.SetStateAction<buyTOUList[]>>
  children?: React.ReactNode
  showRecExcludedWarning?: boolean
  showRecIncludedWarning?: boolean
  buyCost?: ISeasonalPVCharge
  buyCostList?: {
    touId: string
    custId: string
    annualGeneration: number
    generationCost: number
  }[]
}

export type buyTOUList = {
  rowId: string
  _id: string
  name: string | string[]
  cust: {
    _id: string | string[]
    name: string | string[]
    facilityCapacity?: number
    previousMethod?: string
    revenueContractType?: string
    contractPrice?: number
    RECWeight?: number
  }
  annualGeneration?: number | undefined
  generationCost?: number | undefined
}
// | undefined
//#endregion

const createEmptyBuyTOURow = (): buyTOUList => ({
  rowId: crypto.randomUUID(),
  _id: '',
  name: '',
  cust: {
    _id: '',
    name: '',
  },
})

const formatOptionalNumber = (value: number | undefined) =>
  typeof value === 'number' && Number.isFinite(value)
    ? Number(value.toFixed(0)).toLocaleString()
    : '-'

const getSelectedBuyTOUList = (list: buyTOUList[]) =>
  list.filter((item) => item._id && item._id !== 'default')

const findAllCustomerGQL = gql`
  query findAllPVcust {
    findAllPVcust {
      _id
      name
      facilityCapacity
      previousMethod
      revenueContractType
      contractPrice
      RECWeight
    }
  }
`

const findAllBuyTOUGQL = gql`
  query findAllBuyTOU {
    findAllBuyTOU {
      _id
      name
      custId
    }
  }
`

export const TotalPurchaseValue = (props: TotalPurchaseValuProps) => {
  const { buyTOUList, setBuyTOUList } = props
  const [buyTOU, setBuyTou] = useState<buyTOUList[]>(
    buyTOUList.length > 0 ? buyTOUList : [createEmptyBuyTOURow()]
  )
  const [planNameList, setPlanNameList] = useState<PlanLists[]>()
  const [customerNameList, setCustomerNameList] = useState<CustomerLists[]>()
  const { data: originSellTOUList } = useQuery<{
    findAllBuyTOU: PlanLists[]
  }>(findAllBuyTOUGQL)
  const { data: originCustomerNameList } = useQuery<{
    findAllPVcust: CustomerLists[]
  }>(findAllCustomerGQL)
  const [selectedPlan, setSelectedPlan] = useState<{ [key: number]: string }>(
    {}
  )

  useEffect(() => {
    if (buyTOUList && buyTOUList.length > 0) {
      setPlanNameList(
        buyTOUList.map(
          (v) =>
            ({
              _id: v?._id,
              name: v?.name,
              custId: v?.cust?._id as string,
            }) as { _id: string; name: string; custId?: string }
        )
      )
      setCustomerNameList(
        buyTOUList.map(
          (v) =>
            ({ _id: v?.cust?._id, name: v?.cust?.name }) as {
              _id: string
              name: string
            }
        )
      )
    } else {
      setPlanNameList(originSellTOUList?.findAllBuyTOU)
      setCustomerNameList(originCustomerNameList?.findAllPVcust)
    }
  }, [originCustomerNameList?.findAllPVcust, originSellTOUList?.findAllBuyTOU])

  const handlePlanSelect = (
    e: React.ChangeEvent<HTMLSelectElement>,
    index: number
  ) => {
    e.preventDefault()
    const selectedId = e.target.value
    const selectedPlanInfo = planNameList?.find((m) => m._id === selectedId)
    const selectedCustomerInfo = customerNameList?.find(
      (m) => m._id === selectedPlanInfo?.custId
    )

    const updatedList = buyTOU?.map((item, idx) => {
      if (idx === index) {
        return {
          ...item,
          _id: selectedId,
          name: selectedPlanInfo?.name || '',
          cust: {
            _id: selectedPlanInfo?.custId || '',
            name: selectedCustomerInfo?.name || '',
            facilityCapacity: selectedCustomerInfo?.facilityCapacity,
            previousMethod: selectedCustomerInfo?.previousMethod,
            revenueContractType: selectedCustomerInfo?.revenueContractType,
            contractPrice: selectedCustomerInfo?.contractPrice,
            RECWeight: selectedCustomerInfo?.RECWeight,
          },
        }
      }
      return item
    })

    setBuyTou(updatedList)
    setBuyTOUList(getSelectedBuyTOUList(updatedList))

    setSelectedPlan({
      ...selectedPlan,
      [index]: selectedId,
    })
  }

  const addRow = () => {
    const updatedList = [...buyTOU, createEmptyBuyTOURow()]
    setBuyTou(updatedList)
  }

  const deleteRow = (rowId: string) => {
    const updatedList = buyTOU.filter((row) => row.rowId !== rowId)
    const nextList =
      updatedList.length > 0 ? updatedList : [createEmptyBuyTOURow()]
    setBuyTou(nextList)
    setBuyTOUList(getSelectedBuyTOUList(nextList))
  }

  let sumBuyCostPV = 0
  let sumBuyCostCharge = 0

  if (props.buyCost) {
    sumBuyCostPV =
      props.buyCost.spr.pv +
      props.buyCost.sum.pv +
      props.buyCost.fall.pv +
      props.buyCost.win.pv

    sumBuyCostCharge =
      props.buyCost.spr.charge +
      props.buyCost.sum.charge +
      props.buyCost.fall.charge +
      props.buyCost.win.charge
  }

  const averagePurchaseCost =
    sumBuyCostPV > 0 ? Math.round(sumBuyCostCharge / sumBuyCostPV) : 0

  return (
    <StyledTotalPurchaseValue>
      <div className="title">
        <span className="vertical-line" />
        PV 전력 구매처 선택
      </div>
      {props.showRecExcludedWarning ? (
        <div className="warning-message">
          REC 미포함 요금제가 선택되어 있습니다
        </div>
      ) : null}
      {props.showRecIncludedWarning ? (
        <div className="warning-message">
          REC 포함 요금제가 선택되어 있습니다
        </div>
      ) : null}
      <table>
        <thead>
          <tr>
            <th></th>
            <th>요금제</th>
            <th>고객명</th>
            <th>연간 발전량 [kWh]</th>
            <th>발전 비용 [원]</th>
          </tr>
        </thead>
        <tbody>
          {buyTOU?.map((m, index) => (
            <tr key={m.rowId}>
              <th
                className="delete-btn"
                onClick={() => deleteRow(m?.rowId as string)}
              >
                <TrashCanIcon />
              </th>
              <td className="accordion-col">
                <StyledSelect
                  id="plan-list"
                  onChange={(e) => handlePlanSelect(e, index)}
                  isSelected={!!selectedPlan[index]}
                >
                  <option value="default">선택해주세요</option>
                  {planNameList?.map((plan) => (
                    <option
                      id={plan._id}
                      key={plan._id}
                      value={plan._id}
                      selected={m?._id === plan._id}
                    >
                      {plan.name}
                    </option>
                  ))}
                </StyledSelect>
              </td>
              <td className="accordion-col">
                {m?._id && typeof m?.cust?.name === 'string'
                  ? m.cust.name
                  : '-'}
              </td>
              <td>
                {formatOptionalNumber(
                  props.buyCostList?.find(
                    (item) =>
                      item.custId === m?.cust?._id && item.touId === m?._id
                  )?.annualGeneration
                )}
              </td>
              <td>
                {formatOptionalNumber(
                  props.buyCostList?.find(
                    (item) =>
                      item.custId === m?.cust?._id && item.touId === m?._id
                  )?.generationCost
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="add-row-wrapper">
        <AddBtn onAdd={addRow} />
      </div>
      <div className="total-section">
        <div className="total-generation">
          <span className="subtitle">총 발전량</span>
          <div className="presentation-section">
            <span>{Number(sumBuyCostPV.toFixed(0)).toLocaleString()}</span>
            <span> kWh</span>
          </div>
        </div>
        <div className="total-value">
          <span className="subtitle">총 발전비용</span>
          <div className="presentation-section">
            <span>{Number(sumBuyCostCharge.toFixed(0)).toLocaleString()}</span>
            <span> 원</span>
          </div>
        </div>
        <div className="total-average">
          <span className="subtitle">평균</span>
          <div className="presentation-section">
            <span>{averagePurchaseCost.toLocaleString()}</span>
            <span> 원/kWh</span>
          </div>
        </div>
      </div>
      <PurchaseUnitComparisonTitle>
        판매요금제 유형별 설계 가능 구매단가
      </PurchaseUnitComparisonTitle>
      {props.children}
    </StyledTotalPurchaseValue>
  )
}
